import { useEffect, useRef } from "react";
import {
  State,
  initialState,
  Actions,
  ActionTypes,
  reducer,
  UploadState
} from "./State";
import { useTranslation } from "utilities/i18n";
import { DISMISS, useNotification } from "collections";
import {
  useFetchDevicePcapQuery,
  useRequestStartUploadPcapMutation,
  useRequestStopUploadPcapMutation
} from "generated/graphql";
import { useTypesafeActions } from "store";
import { format, differenceInMilliseconds, isAfter } from "date-fns";
import { DATE_FORMAT } from "Constants";

export function usePcapUploadLogic(
  deviceId: string,
  timeout: number,
  delay?: number
) {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [, requestStartUploadPCAP] = useRequestStartUploadPcapMutation();
  const [, requestStopUploadPCAP] = useRequestStopUploadPcapMutation();

  const [
    {
      uploadState,
      downloadLink,
      lastUploadRequestTime,
      uploadedOn,
      uploadTimeout,
      uploadFailed
    },
    actions
  ] = useTypesafeActions<State, ActionTypes>(reducer, initialState, Actions);
  const interval = delay ? delay : 5000;
  const uploadingOrStopping = uploadState !== UploadState.Idle;
  const { data, executor } = useFetchDevicePcapQuery({
    variables: {
      deviceId
    },
    pause: !uploadingOrStopping,
    requestPolicy: "network-only",
    pollInterval: uploadingOrStopping ? interval : undefined
  });

  // Fire the query right away when the page loads.
  useEffect(() => {
    if (!uploadingOrStopping) {
      executor({ requestPolicy: "network-only" });
    }
  }, [uploadingOrStopping, executor]);

  let downloadUrl = data?.accessPCAPUrl.url
    ? data?.accessPCAPUrl.url
    : downloadLink
    ? downloadLink
    : "";
  let uploadedOnString = data?.accessPCAPUrl.uploadedOn
    ? data.accessPCAPUrl.uploadedOn
    : uploadedOn
    ? uploadedOn
    : "";

  const stopTime = data?.accessPCAPUrl.pcapStopTime;
  const isStopTimeAfter = isAfter(new Date(stopTime), new Date(Date.now()));

  useEffect(() => {
    if (isStopTimeAfter) {
      // The new timeout should be the difference between now and the stopTime
      const newTimeout = differenceInMilliseconds(
        new Date(stopTime),
        new Date(Date.now())
      );
      actions.updateWaitingAndRequest({
        uploadState: UploadState.Uploading,
        lastUploadRequestTime: new Date().toISOString(),
        uploadTimeout: newTimeout
      });
    }
  }, [isStopTimeAfter, actions, timeout, stopTime]);
  // If uploadedOnString is a blank string, this value will be NaN
  let timeDifferenceFromLastUpload = differenceInMilliseconds(
    new Date(uploadedOnString),
    new Date(lastUploadRequestTime)
  );

  useEffect(() => {
    actions.updateLinkAndTime({
      downloadLink: downloadUrl,
      uploadedOn: uploadedOnString
    });
  }, [downloadUrl, actions, uploadedOnString]);

  let timer = useRef<number>(0);

  useEffect(() => {
    if (
      lastUploadRequestTime &&
      timeDifferenceFromLastUpload > 0 &&
      uploadingOrStopping
    ) {
      actions.updateUploadState(UploadState.Idle);
      addNotification({
        id: "device-pcap-fetch",
        type: "success",
        message: t("PCAP_SUCCESS_MESSAGE"),
        dismiss: DISMISS.DEFAULT
      });
      clearTimeout(timer.current);
    }
  }, [
    lastUploadRequestTime,
    timeDifferenceFromLastUpload,
    uploadingOrStopping,
    actions,
    addNotification,
    t
  ]);

  useEffect(() => {
    let timeRemaining = timeout;
    if (isStopTimeAfter) {
      timeRemaining = differenceInMilliseconds(
        new Date(stopTime),
        new Date(Date.now())
      );
    }
    if (uploadingOrStopping) {
      timer.current = window.setTimeout(() => {
        actions.updateUploadState(UploadState.Idle);
        actions.updateUploadFailed(true);
      }, +timeRemaining + +uploadTimeout);
      return () => {
        clearTimeout(timer.current);
      };
    }
    return () => {};
  }, [
    uploadingOrStopping,
    uploadTimeout,
    timeout,
    actions,
    isStopTimeAfter,
    stopTime
  ]);

  useEffect(() => {
    if (uploadFailed) {
      actions.updateUploadFailed(false);
      addNotification({
        id: "device-pcap-fetch",
        type: "error",
        message: t("PCAP_ERROR"),
        dismiss: DISMISS.DEFAULT
      });
    }
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFailed, actions]);

  const handleRequestUploadToggle = async (timeout: number) => {
    if (!uploadingOrStopping) {
      const filter = "ether host {PHONE_MAC_ADDRESS}";
      const uploadInterval = timeout;
      const size = 5000;
      const { error } = await requestStartUploadPCAP({
        filter,
        interval: uploadInterval,
        timeout,
        size,
        deviceId
      });
      if (!error) {
        actions.updateWaitingAndRequest({
          uploadState: UploadState.Uploading,
          lastUploadRequestTime: new Date().toISOString(),
          uploadTimeout: timeout * 1000
        });
      }
    } else {
      await requestStopUploadPCAP({ deviceId });
      actions.updateUploadState(UploadState.Stopping);
    }
  };

  if (data?.accessPCAPUrl.uploadedOn) {
    uploadedOnString = format(
      new Date(data.accessPCAPUrl.uploadedOn),
      DATE_FORMAT.WITH_TIME_LONG
    );
  }
  return {
    handleRequestUploadToggle,
    downloadUrl,
    uploadState,
    actions,
    stopTime
  };
}
